/* One search result: the matched sentence first, the item second.

   This is the shape of the whole product's search promise — "search what they
   said" — so the transcript excerpt is the headline and the thumbnail, title and
   meta are the footnote identifying which item said it. v1 got this right and it
   is ported as-is.

   Separate from item-tile by ADR 0002 §8: leading with a sentence and leading
   with a picture are different components, not one with a `variant` prop.

   Presentational: emits `open`. */

import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { shadowReset } from '../../design/base.js';
import { apiUrl } from '../../core/api.js';
import { clean, fmtDuration, splitSnippet } from '../../../../packages/core/index.js';
import type { Item, SearchItem } from '../../core/types.js';

@customElement('item-row')
export class ItemRow extends LitElement {
  static override styles = [
    shadowReset,
    css`
      :host {
        display: block;
      }
      button {
        display: block;
        width: 100%;
        padding: var(--space-3) var(--space-3);
        border: 0;
        border-radius: var(--radius-md);
        background: none;
        color: inherit;
        font: inherit;
        font-family: var(--font-ui);
        text-align: left;
        cursor: pointer;
        transition: background-color 120ms ease;
      }
      /* Guarded: on touch a hover latches and outlives the tap (see g-chips), and
         a row left lit reads as selected. */
      @media (hover: hover) {
        button:hover {
          background: var(--surface);
        }
      }
      button:active {
        background: var(--press);
      }
      button:focus-visible {
        outline: none;
        box-shadow: var(--ring);
      }
      /* Serif italic, and larger than anything else in the row. It is a quotation
         of speech, and the voice change is what separates it from the app's own
         UI text at a glance. */
      .spoken {
        margin: 0 0 var(--space-2);
        /* --font-text is loaded italic-only, so the style is not synthesised. */
        font-family: var(--font-text);
        font-style: italic;
        font-size: 1.05rem;
        line-height: 1.45;
        /* Measure, not width. A 1,800px line of prose is unreadable however much
           room the workspace has. */
        max-width: 62ch;
      }
      mark {
        padding: 0 2px;
        border-radius: 3px;
        background: color-mix(in oklab, var(--accent) 22%, transparent);
        color: var(--accent);
      }
      .meta {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }
      .thumb {
        flex: none;
        width: 34px;
        height: 44px;
        border-radius: var(--radius-sm);
        background: var(--surface-2);
        object-fit: cover;
      }
      .title {
        font-size: 0.8rem;
        font-weight: 600;
        line-height: 1.3;
      }
      .sub {
        color: var(--text-dim);
        font-family: var(--font-mono);
        font-size: 0.66rem;
      }
    `,
  ];

  /** Either list feeds this row; only the search one carries a `snippet`. */
  @property({ attribute: false }) item!: Item | SearchItem;

  /** category · @creator · duration, skipping whatever this item lacks. */
  private get sub(): string {
    const creator = clean(this.item.creator);
    return [
      clean(this.item.category),
      creator ? `@${creator}` : '',
      this.item.duration_s ? fmtDuration(this.item.duration_s) : '',
    ]
      .filter(Boolean)
      .join(' · ');
  }

  override render() {
    const it = this.item;
    if (!it) return html``;
    // Rendered as child nodes, not as an HTML string: FTS hands back transcript
    // text with match markers in it, and building <mark> tags by replacement is
    // one ordering mistake away from injecting that text as markup.
    const parts = splitSnippet(('snippet' in it ? it.snippet : '') ?? '');
    return html`
      <button @click=${() => this.dispatchEvent(
        new CustomEvent('open', { bubbles: true, composed: true }),
      )}>
        ${parts.length
          ? html`<p class="spoken">
              “${parts.map((p) => (p.mark ? html`<mark>${p.text}</mark>` : p.text))}”
            </p>`
          : ''}
        <span class="meta">
          ${it.thumb
            ? html`<img class="thumb" loading="lazy" src=${apiUrl(it.thumb)} alt="" />`
            : html`<span class="thumb"></span>`}
          <span>
            <span class="title">${clean(it.title) || 'Untitled'}</span><br />
            <span class="sub">${this.sub}</span>
          </span>
        </span>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'item-row': ItemRow;
  }
}
